import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, deleteDoc, writeBatch, arrayUnion, arrayRemove, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import { UserRepository } from '../../../domain/users/repositories/UserRepository';
import { User } from '../../../domain/users/entities/User';
import { getFirestore } from 'firebase/firestore';
import { geohashForLocation } from 'geofire-common';

/**
 * Extrait les coordonnées d'un objet userData selon les différentes structures possibles
 * @param {Object} userData - Les données utilisateur
 * @returns {Object|null} - {lat, lng} ou null si pas de coordonnées valides
 */
function extractCoordinates(userData) {
  let lat, lng;
  
  if (userData.location?.coordinates?.latitude && userData.location?.coordinates?.longitude) {
    lat = userData.location.coordinates.latitude;
    lng = userData.location.coordinates.longitude;
  } else if (userData.location?.lat && userData.location?.lng) {
    lat = userData.location.lat;
    lng = userData.location.lng;
  } else if (userData.location?.location?.lat && userData.location?.location?.lng) {
    lat = userData.location.location.lat;
    lng = userData.location.location.lng;
  }
  
  return (lat != null && lng != null) ? { lat, lng } : null;
}

export class FirestoreUserRepository extends UserRepository {
  constructor() {
    super();
    this.collection = collection(db, 'users');
  }

  async getUser(uid) {
    try {
      if (!uid) {
        throw new Error('User ID is required');
      }
      
      const userDoc = await getDoc(doc(this.collection, uid));
      
      if (!userDoc.exists()) {
        console.error(`User with ID ${uid} not found in Firestore`);
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      
      return new User({
        uid,
        ...userData
      });
    } catch (error) {
      console.error(`Error getting user ${uid}:`, error);
      throw error;
    }
  }

  async updateUser(uid, userData) {
    try {
      // Si la localisation est mise à jour, calculer le geohash
      if (userData.location) {
        // Extraire les coordonnées et générer le geohash si valides
        const coordinates = extractCoordinates(userData);
        if (coordinates) {
          userData.geohash = geohashForLocation([coordinates.lat, coordinates.lng]);
          if (process.env.NODE_ENV === 'development') {
            console.log(`🌍 Geohash généré pour l'utilisateur ${uid}: ${userData.geohash}`);
          }
        }
      }
      
      const userRef = doc(this.collection, uid);
      await updateDoc(userRef, userData);
      return true;
    } catch (error) {
      throw error;
    }
  }

  async getUserPosts(uid) {
    try {
      const postsQuery = query(
        collection(db, 'posts'),
        where('author.uid', '==', uid),
        orderBy('activity.date', 'desc') // Activités les plus récentes en premier
      );
      const snapshot = await getDocs(postsQuery);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          activity: data.activity ? {
            ...data.activity,
            date: data.activity.date?.toDate ? data.activity.date.toDate() : data.activity.date
          } : null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
        };
      });
    } catch (error) {
      // Si erreur d'index, fallback sans tri
      if (error.code === 'failed-precondition') {
        console.warn('Index manquant pour tri des posts, fallback sans tri');
        const fallbackQuery = query(
          collection(db, 'posts'),
          where('author.uid', '==', uid)
        );
        const snapshot = await getDocs(fallbackQuery);
        const posts = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            activity: data.activity ? {
              ...data.activity,
              date: data.activity.date?.toDate ? data.activity.date.toDate() : data.activity.date
            } : null,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
          };
        });
        // Tri côté client si pas d'index (par date d'activité)
        return posts.sort((a, b) => {
          const dateA = a.activity?.date || a.createdAt;
          const dateB = b.activity?.date || b.createdAt;
          return dateB - dateA;
        });
      }
      throw error;
    }
  }

  async getUserParticipations(uid) {
    try {
      const postsQuery = query(
        collection(db, 'posts'),
        where('activity.participants', 'array-contains', uid),
        orderBy('activity.date', 'desc') // Activités les plus récentes en premier
      );
      const snapshot = await getDocs(postsQuery);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          activity: data.activity ? {
            ...data.activity,
            date: data.activity.date?.toDate ? data.activity.date.toDate() : data.activity.date
          } : null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
        };
      });
    } catch (error) {
      // Si erreur d'index, fallback sans tri
      if (error.code === 'failed-precondition') {
        console.warn('Index manquant pour tri des participations, fallback sans tri');
        const fallbackQuery = query(
          collection(db, 'posts'),
          where('activity.participants', 'array-contains', uid)
        );
        const snapshot = await getDocs(fallbackQuery);
        const posts = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            activity: data.activity ? {
              ...data.activity,
              date: data.activity.date?.toDate ? data.activity.date.toDate() : data.activity.date
            } : null,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
          };
        });
        // Tri côté client si pas d'index (par date d'activité)
        return posts.sort((a, b) => {
          const dateA = a.activity?.date || a.createdAt;
          const dateB = b.activity?.date || b.createdAt;
          return dateB - dateA;
        });
      }
      throw error;
    }
  }

  async createUser(user) {
    try {
      if (!user?.uid) {
        throw new Error('User ID is required');
      }
      await setDoc(doc(this.collection, user.uid), {
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        bio: user.bio || '',
        location: user.location || '',
        joinDate: user.joinDate,
        following: user.following || [],
        followers: user.followers || [],
        participatingActivities: user.participatingActivities || [],
        createdActivities: user.createdActivities || []
      });
      return user;
    } catch (error) {
      throw error;
    }
  }

  async getUserPreferences(uid) {
    try {
      if (!uid) {
        throw new Error('User ID is required');
      }

      const preferencesDoc = await getDoc(doc(this.collection, uid, 'preferences', 'settings'));
      
      if (!preferencesDoc.exists()) {
        return null;
      }

      return preferencesDoc.data();
    } catch (error) {
      console.error('Error in getUserPreferences:', error);
      throw error;
    }
  }

  async updateUserPreferences(uid, preferences) {
    try {
      if (!uid) {
        throw new Error('User ID is required');
      }

      // Nettoyer les préférences en supprimant les valeurs undefined
      const cleanPreferences = Object.entries(preferences).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const preferencesRef = doc(this.collection, uid, 'preferences', 'settings');
      await setDoc(preferencesRef, cleanPreferences, { merge: true });
      return true;
    } catch (error) {
      console.error('Error in updateUserPreferences:', error);
      throw error;
    }
  }

  /**
   * Permettre à un utilisateur d'en suivre un autre (après acceptation)
   * Cette méthode est appelée après l'acceptation d'une demande
   * @param {string} currentUserId - ID de l'utilisateur qui suit
   * @param {string} targetUserId - ID de l'utilisateur à suivre
   * @returns {Promise<boolean>}
   */
  async followUser(currentUserId, targetUserId) {
    try {
      if (!currentUserId || !targetUserId) {
        throw new Error('User IDs are required');
      }
      
      const batch = writeBatch(db);
      
      // Ajouter targetUserId à la liste "following" de currentUser
      const currentUserRef = doc(this.collection, currentUserId);
      batch.update(currentUserRef, {
        following: arrayUnion(targetUserId),
        pendingFollowRequests: arrayRemove(targetUserId)
      });
      
      // Ajouter currentUserId à la liste "followers" de targetUser
      const targetUserRef = doc(this.collection, targetUserId);
      batch.update(targetUserRef, {
        followers: arrayUnion(currentUserId),
        followRequests: arrayRemove(currentUserId)
      });
      
      await batch.commit();
      console.log(`${currentUserId} suit maintenant ${targetUserId}`);
      return true;
    } catch (error) {
      console.error('Error in followUser:', error);
      throw error;
    }
  }

  /**
   * Permettre à un utilisateur de ne plus en suivre un autre
   * @param {string} currentUserId - ID de l'utilisateur qui se désabonne
   * @param {string} targetUserId - ID de l'utilisateur à ne plus suivre
   * @returns {Promise<boolean>}
   */
  async unfollowUser(currentUserId, targetUserId) {
    try {
      console.log(`Désabonnement: ${currentUserId} se désabonne de ${targetUserId}`);
      const db = getFirestore();
      
      // Approche séparée pour minimiser les erreurs de permission
      // 1. D'abord, mettre à jour le document de l'utilisateur actuel
      const currentUserRef = doc(db, 'users', currentUserId);
      await updateDoc(currentUserRef, {
        following: arrayRemove(targetUserId)
      });
      
      // 2. Ensuite, essayer de mettre à jour la cible
      try {
        const targetUserRef = doc(db, 'users', targetUserId);
        await updateDoc(targetUserRef, {
          followers: arrayRemove(currentUserId)
        });
      } catch (targetError) {
        console.warn("Impossible de mettre à jour les followers de la cible - ignoré:", targetError);
        // L'utilisateur est désabonné de son côté, donc on considère l'opération comme réussie
      }
      
      // 3. Supprimer les documents de demande s'ils existent
      try {
        const pendingRequestRef = doc(db, `users/${targetUserId}/pendingFollowRequests`, currentUserId);
        await deleteDoc(pendingRequestRef);
      } catch (pendingError) {
        console.warn("Erreur lors de la suppression de la demande en attente:", pendingError);
      }
      
      try {
        const sentRequestRef = doc(db, `users/${currentUserId}/sentFollowRequests`, targetUserId);
        await deleteDoc(sentRequestRef);
      } catch (sentError) {
        console.warn("Erreur lors de la suppression de la demande envoyée:", sentError);
      }
      
      console.log("Désabonnement réussi");
      return true;
    } catch (error) {
      console.error("Erreur lors du désabonnement:", error);
      throw error;
    }
  }

  /**
   * Envoyer une demande d'abonnement à un utilisateur
   * @param {string} currentUserId - ID de l'utilisateur qui envoie la demande
   * @param {string} targetUserId - ID de l'utilisateur cible
   * @returns {Promise<boolean>}
   */
  async sendFollowRequest(currentUserId, targetUserId) {
    try {
      if (!currentUserId || !targetUserId) {
        throw new Error('User IDs are required');
      }
      
      const batch = writeBatch(db);
      
      // Ajouter la demande aux followRequests de l'utilisateur cible
      const targetUserRef = doc(this.collection, targetUserId);
      batch.update(targetUserRef, {
        followRequests: arrayUnion(currentUserId)
      });
      
      // Ajouter aux pendingFollowRequests de l'utilisateur actuel
      const currentUserRef = doc(this.collection, currentUserId);
      batch.update(currentUserRef, {
        pendingFollowRequests: arrayUnion(targetUserId)
      });
      
      // Stocker des informations détaillées dans les sous-collections
      const requestRef = doc(this.collection, targetUserId, 'pendingFollowRequests', currentUserId);
      const requestData = {
        userId: currentUserId,
        timestamp: new Date().toISOString(),
        status: 'pending'
      };
      batch.set(requestRef, requestData);
      
      const sentRequestRef = doc(this.collection, currentUserId, 'sentFollowRequests', targetUserId);
      const sentRequestData = {
        userId: targetUserId,
        timestamp: new Date().toISOString(),
        status: 'pending'
      };
      batch.set(sentRequestRef, sentRequestData);
      
      await batch.commit();
      console.log(`Demande d'abonnement envoyée de ${currentUserId} à ${targetUserId}`);
      return true;
    } catch (error) {
      console.error('Error in sendFollowRequest:', error);
      throw error;
    }
  }

  /**
   * Accepter une demande d'abonnement
   * @param {string} currentUserId - ID de l'utilisateur qui accepte la demande
   * @param {string} requesterId - ID de l'utilisateur qui a fait la demande
   * @returns {Promise<boolean>}
   */
  async acceptFollowRequest(currentUserId, requesterId) {
    try {
      if (!currentUserId || !requesterId) {
        throw new Error('User IDs are required');
      }
      
      const batch = writeBatch(db);
      
      // Mettre à jour les listes d'abonnés/abonnements
      const currentUserRef = doc(this.collection, currentUserId);
      batch.update(currentUserRef, {
        followers: arrayUnion(requesterId),
        followRequests: arrayRemove(requesterId)
      });
      
      const requesterRef = doc(this.collection, requesterId);
      batch.update(requesterRef, {
        following: arrayUnion(currentUserId),
        pendingFollowRequests: arrayRemove(currentUserId)
      });
      
      // Mettre à jour le statut dans les sous-collections
      const requestRef = doc(this.collection, currentUserId, 'pendingFollowRequests', requesterId);
      batch.update(requestRef, { status: 'accepted' });
      
      const sentRequestRef = doc(this.collection, requesterId, 'sentFollowRequests', currentUserId);
      batch.update(sentRequestRef, { status: 'accepted' });
      
      await batch.commit();
      console.log(`Demande d'abonnement de ${requesterId} acceptée par ${currentUserId}`);
      return true;
    } catch (error) {
      console.error('Error in acceptFollowRequest:', error);
      throw error;
    }
  }

  /**
   * Refuser une demande d'abonnement
   * @param {string} currentUserId - ID de l'utilisateur qui refuse la demande
   * @param {string} requesterId - ID de l'utilisateur qui a fait la demande
   * @returns {Promise<boolean>}
   */
  async rejectFollowRequest(currentUserId, requesterId) {
    try {
      if (!currentUserId || !requesterId) {
        throw new Error('User IDs are required');
      }
      
      const batch = writeBatch(db);
      
      // Retirer des listes de demandes
      const currentUserRef = doc(this.collection, currentUserId);
      batch.update(currentUserRef, {
        followRequests: arrayRemove(requesterId)
      });
      
      const requesterRef = doc(this.collection, requesterId);
      batch.update(requesterRef, {
        pendingFollowRequests: arrayRemove(currentUserId)
      });
      
      // Supprimer des sous-collections
      const requestRef = doc(this.collection, currentUserId, 'pendingFollowRequests', requesterId);
      batch.delete(requestRef);
      
      const sentRequestRef = doc(this.collection, requesterId, 'sentFollowRequests', currentUserId);
      batch.delete(sentRequestRef);
      
      await batch.commit();
      console.log(`Demande d'abonnement de ${requesterId} refusée par ${currentUserId}`);
      return true;
    } catch (error) {
      console.error('Error in rejectFollowRequest:', error);
      throw error;
    }
  }

  /**
   * Annuler une demande d'abonnement envoyée
   * @param {string} currentUserId - ID de l'utilisateur qui annule la demande
   * @param {string} targetUserId - ID de l'utilisateur cible
   * @returns {Promise<boolean>}
   */
  async cancelFollowRequest(currentUserId, targetUserId) {
    try {
      if (!currentUserId || !targetUserId) {
        throw new Error('User IDs are required');
      }
      
      const batch = writeBatch(db);
      
      // Retirer des listes de demandes
      const targetUserRef = doc(this.collection, targetUserId);
      batch.update(targetUserRef, {
        followRequests: arrayRemove(currentUserId)
      });
      
      const currentUserRef = doc(this.collection, currentUserId);
      batch.update(currentUserRef, {
        pendingFollowRequests: arrayRemove(targetUserId)
      });
      
      // Supprimer des sous-collections
      const requestRef = doc(this.collection, targetUserId, 'pendingFollowRequests', currentUserId);
      batch.delete(requestRef);
      
      const sentRequestRef = doc(this.collection, currentUserId, 'sentFollowRequests', targetUserId);
      batch.delete(sentRequestRef);
      
      await batch.commit();
      console.log(`Demande d'abonnement annulée de ${currentUserId} à ${targetUserId}`);
      return true;
    } catch (error) {
      console.error('Error in cancelFollowRequest:', error);
      throw error;
    }
  }

  /**
   * Récupérer les demandes d'abonnement en attente envoyées par un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<string[]>} - Liste des utilisateurs à qui une demande a été envoyée
   */
  async getPendingFollowRequests(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      const sentRequestsCollection = collection(this.collection, userId, 'sentFollowRequests');
      const snapshot = await getDocs(sentRequestsCollection);
      
      return snapshot.docs.map(doc => doc.id);
    } catch (error) {
      console.error('Error in getPendingFollowRequests:', error);
      throw error;
    }
  }

  /**
   * Retire un abonné de la liste des followers de l'utilisateur courant
   * @param {string} currentUserId - ID de l'utilisateur courant
   * @param {string} followerId - ID de l'abonné à retirer
   * @returns {Promise<boolean>} - Résultat de l'opération
   */
  async removeFollower(currentUserId, followerId) {
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      
      // Retirer l'abonné de la liste des followers de l'utilisateur courant
      const currentUserRef = doc(db, 'users', currentUserId);
      batch.update(currentUserRef, {
        followers: arrayRemove(followerId)
      });
      
      // Retirer l'utilisateur courant de la liste des following de l'abonné
      const followerRef = doc(db, 'users', followerId);
      batch.update(followerRef, {
        following: arrayRemove(currentUserId)
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Erreur dans removeFollower:', error);
      throw error;
    }
  }

  async checkUserExists(uid) {
    try {
      if (!uid) {
        return false;
      }
      
      const userDoc = await getDoc(doc(this.collection, uid));
      return userDoc.exists();
    } catch (error) {
      return false;
    }
  }
} 